/* tslint:disable:no-string-literal object-literal-shorthand */
import { Injectable } from '@angular/core';
import { CorpusMC } from 'src/app/models/corpusMC';
import { Author } from 'src/app/models/author';
import { TextRange } from 'src/app/models/textRange';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';
import { ToastController } from '@ionic/angular';
import { HelperService } from 'src/app/helper.service';
import {
    CaseTranslations,
    CaseValue,
    DependencyTranslation,
    DependencyValue,
    ExerciseType,
    ExerciseTypeTranslation,
    FeatsKey,
    InstructionsTranslation,
    PartOfSpeechTranslation,
    PartOfSpeechValue,
} from 'src/app/models/enum';
import { QueryMC } from 'src/app/models/queryMC';
import { Exercise } from 'src/app/models/exercise';
import { Feedback } from 'src/app/models/feedback';
import { PhenomenonMap, PhenomenonMapContent } from 'src/app/models/phenomenonMap';
import { ReplaySubject } from 'rxjs';
import { ApplicationState } from './models/applicationState';
import { take } from 'rxjs/operators';
import { Storage } from '@ionic/storage';
import { UpdateInfo } from './models/updateInfo';
import configMC from '../configMC';
import {
    AnnisResponse,
    FrequencyItem,
    Link,
    NodeMC,
    RawTextFormExtension,
    Solution
} from '../../openapi';
import { Phenomenon } from '../../openapi';

@Injectable({
    providedIn: 'root'
})
export class CorpusService {
    public annisResponse: AnnisResponse;
    public availableCorpora: CorpusMC[];
    public availableAuthors: Author[] = [];
    public baseUrn: string;
    public citationsUnavailableString: string;
    public corporaUnavailableString: string;
    public currentAuthor: Author;
    public currentCorpus: ReplaySubject<CorpusMC>;
    private currentCorpusCache: CorpusMC;
    public currentSolutions: Solution[];
    public currentText = '';
    public currentTextRange: ReplaySubject<TextRange>;
    private currentTextRangeCache: TextRange = new TextRange({ start: ['', '', ''], end: ['', '', ''] });
    public currentUrn = '';
    public dataAlreadySentMessage: string;
    public dataSentSuccessMessage: string;
    public downloadStartsShortlyString: string;
    public emptyQueryValueString: string;
    public exercise: Exercise = new Exercise({
        type: ExerciseType.cloze,
        typeTranslation: '',
        queryItems: [new QueryMC({
            availableValues: [],
            phenomenon: Phenomenon.Upos,
            selectedValues: [PartOfSpeechValue.adjective],
        })],
        feedback: new Feedback({ general: '', incorrect: '', partiallyCorrect: '', correct: '' }),
        instructionsTranslation: ''
    });
    public invalidQueryCorpusString: string;
    public invalidSentenceCountString: string;
    public invalidTextRangeString: string;
    public isInitialized: ReplaySubject<boolean>;
    public isTextRangeCorrect = false;
    public myTextString: string;
    public phenomenonMap: PhenomenonMap = new PhenomenonMap({
        dependency: new PhenomenonMapContent({ translationObject: DependencyTranslation }),
        feats: new PhenomenonMapContent({ translationObject: CaseTranslations }),
        lemma: new PhenomenonMapContent({ translationObject: null }),
        upos: new PhenomenonMapContent({ translationObject: PartOfSpeechTranslation })
    });
    public previewAnnisResponse: AnnisResponse;
    public searchRegexMissingString: string;
    public shareLinkCopiedString: string;
    public textTooLongString: string;
    public tooManyHitsString: string;

    constructor(
        public http: HttpClient,
        public toastCtrl: ToastController,
        public helperService: HelperService,
        public storage: Storage,
        public translate: TranslateService,
    ) {
    }

    adjustQueryValue(query: QueryMC, queryIndex: number): void {
        this.getSortedQueryValues(query, queryIndex);
        // when the phenomenon changes, choose the first value from the translated list as the default
        query.selectedValues = query.availableValues.slice(0, 1);
        this.updateBaseWord(query, queryIndex);
    }

    /**
     * Adjusts the translations for the exercise type and instructions.
     * Also adjusts the query values for matching exercises.
     */
    adjustTranslations(): Promise<void> {
        return new Promise<void>(resolve => {
            this.translate.get(ExerciseTypeTranslation[this.exercise.type]).subscribe(
                value => this.exercise.typeTranslation = value);
            if ([ExerciseType.cloze, ExerciseType.matching, ExerciseType.markWords].indexOf(this.exercise.type) > -1) {
                this.translate.get(InstructionsTranslation[this.exercise.type]).subscribe(
                    value => this.exercise.instructionsTranslation = value);
            }
            if (this.exercise.type === ExerciseType.matching) {
                this.exercise.queryItems = [new QueryMC({ phenomenon: Phenomenon.Upos, selectedValues: [] }),
                new QueryMC({ phenomenon: Phenomenon.Upos, selectedValues: [] })];
                this.getFrequencyAnalysis().then(() => {
                    this.adjustQueryValue(this.exercise.queryItems[0], 0);
                    this.adjustQueryValue(this.exercise.queryItems[1], 1);
                    return resolve();
                });
            } else if (this.exercise.queryItems.length > 1) {
                this.exercise.queryItems.splice(1, 1);
                return resolve();
            }
        }).finally(() => this.adjustQueryValue(this.exercise.queryItems[0], 0));
    }

    checkAnnisResponse(isPreview: boolean = false): Promise<void> {
        return new Promise((resolve, reject) => {
            if (this.annisResponse || (isPreview && this.previewAnnisResponse)) {
                return resolve();
            }
            this.helperService.applicationState.pipe(take(1)).subscribe((state: ApplicationState) => {
                this.previewAnnisResponse = state.previewAnnisResponse;
                this.annisResponse = state.mostRecentSetup.annisResponse;
                this.helperService.isVocabularyCheck = [this.previewAnnisResponse, this.annisResponse].some(
                    (ar: AnnisResponse) => ar && ar.graph_data && ar.graph_data.nodes && ar.graph_data.nodes.some(
                        (x: NodeMC) => x.is_oov));
                this.currentAuthor = state.mostRecentSetup.currentAuthor;
                this.currentUrn = state.mostRecentSetup.currentUrn;
                this.currentCorpusCache = state.mostRecentSetup.currentCorpus;
                return this.annisResponse ? resolve() : reject();
            }, () => {
                return reject();
            });
        });
    }

    checkForUpdates(): Promise<void> {
        return new Promise((resolve, reject) => {
            this.storage.get(configMC.localStorageKeyUpdateInfo).then((jsonString: string) => {
                // check local storage for necessary updates
                const updateInfo: UpdateInfo = JSON.parse(jsonString) as UpdateInfo;
                this.getCorpora(updateInfo ? updateInfo.corpora : 0)
                    .then(() => {
                        return resolve();
                    }, () => {
                        return reject();
                    });
            });
        });
    }

    getAnnotationsForCustomText(): Promise<AnnisResponse> {
        return new Promise(((resolve, reject) => {
            const url: string = configMC.backendBaseUrl + configMC.backendApiRawtextPath;
            const rtfe: RawTextFormExtension = {
                plain_text: this.currentText,
            };
            const body: HttpParams = this.helperService.getFormForPostRequest(rtfe);
            this.helperService.makePostRequest(this.http, this.toastCtrl, url, body).then((ar: AnnisResponse) => {
                return resolve(ar);
            }, (error: HttpErrorResponse) => {
                return reject(error);
            });
        }));
    }

    getCorpora(lastUpdateTimeMS: number = 0): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            this.availableCorpora = [];
            this.availableAuthors = [];
            // get corpora from REST API
            const url: string = configMC.backendBaseUrl + configMC.backendApiCorporaPath;
            const params: HttpParams = new HttpParams().set('last_update_time', lastUpdateTimeMS.toString());
            this.helperService.makeGetRequest(this.http, this.toastCtrl, url, params, this.corporaUnavailableString)
                .then((data: object[]) => {
                    if (data) {
                        const corpusList: CorpusMC[] = data as CorpusMC[];
                        this.storage.set(configMC.localStorageKeyCorpora, JSON.stringify(corpusList)).then();
                        this.storage.get(configMC.localStorageKeyUpdateInfo).then((jsonString: string) => {
                            const updateInfo: UpdateInfo = JSON.parse(jsonString) as UpdateInfo;
                            updateInfo.corpora = new Date().getTime();
                            this.storage.set(configMC.localStorageKeyUpdateInfo, JSON.stringify(updateInfo)).then();
                        });
                        this.processCorpora(corpusList);
                        return resolve();
                    } else {
                        this.loadCorporaFromLocalStorage().then(() => {
                            return resolve();
                        });
                    }
                }, async (error: HttpErrorResponse) => {
                    this.loadCorporaFromLocalStorage().then(() => {
                        return reject();
                    });
                });
        });
    }

    getCorpusListFromJSONstring(jsonString: string): CorpusMC[] {
        let jsonObject: object = JSON.parse(jsonString);
        // backwards compatibility
        const corpusProp = 'corpora';
        jsonObject = jsonObject.hasOwnProperty(corpusProp) ? jsonObject[corpusProp] : jsonObject;
        return jsonObject as CorpusMC[];
    }

    getCTStextPassage(urn: string): Promise<AnnisResponse> {
        return new Promise(((resolve, reject) => {
            const url: string = configMC.backendBaseUrl + configMC.backendApiRawtextPath;
            const params: HttpParams = new HttpParams().set('urn', urn);
            this.helperService.makeGetRequest(this.http, this.toastCtrl, url, params).then((ar: AnnisResponse) => {
                return resolve(ar);
            }, (error: HttpErrorResponse) => {
                return reject(error);
            });
        }));
    }

    getCTSvalidReff(urn: string): Promise<string[]> {
        return new Promise((resolve, reject) => {
            const fullUrl: string = configMC.backendBaseUrl + configMC.backendApiValidReffPath;
            const params: HttpParams = new HttpParams().set('urn', urn);
            this.helperService.makeGetRequest(this.http, this.toastCtrl, fullUrl, params).then((reff: string[]) => {
                resolve(reff);
            }, (error: HttpErrorResponse) => {
                reject(error);
            });
        });
    }

    getFrequencyAnalysis(): Promise<void> {
        return new Promise((resolve, reject) => {
            if (this.annisResponse.frequency_analysis && this.annisResponse.frequency_analysis.length) {
                return resolve();
            } else {
                const url: string = configMC.backendBaseUrl + configMC.backendApiFrequencyPath;
                const params: HttpParams = new HttpParams().set('urn', this.currentUrn);
                this.helperService.makeGetRequest(this.http, this.toastCtrl, url, params).then((fis: FrequencyItem[]) => {
                    this.annisResponse.frequency_analysis = fis;
                    this.helperService.applicationState.pipe(take(1)).subscribe((state: ApplicationState) => {
                        state.mostRecentSetup.annisResponse = this.annisResponse;
                        this.helperService.saveApplicationState(state).then();
                    });
                    return resolve();
                }, () => {
                    return reject();
                });
            }
        });
    }

    getSortedQueryValues(query: QueryMC, queryIndex: number): void {
        const pmc: PhenomenonMapContent = this.phenomenonMap[query.phenomenon];
        if (this.exercise.type === ExerciseType.matching) {
            if (!this.annisResponse.frequency_analysis) {
                query.availableValues = [];
                return;
            }
            if (queryIndex) {
                const relevantFIs: FrequencyItem[] = this.annisResponse.frequency_analysis.filter(
                    x => x.values[0] === this.exercise.queryItems[0].selectedValues[0] &&
                        x.phenomena[1] === query.phenomenon);
                query.availableValues = Array.from(new Set<string>(relevantFIs.map(x => x.values[1])))
                    .sort((a, b) => {
                        return pmc.translationValues[a] < pmc.translationValues[b] ? -1 : 1;
                    });
                return;
            } else {
                const relevantFIs: FrequencyItem[] = this.annisResponse.frequency_analysis.filter(
                    x => x.phenomena[0] === query.phenomenon);
                query.availableValues = Array.from(new Set<string>(relevantFIs.map(x => x.values[0])))
                    .sort((a, b) => {
                        return pmc.translationValues[a] < pmc.translationValues[b] ? -1 : 1;
                    });
                return;
            }
        }
        if (!pmc.specificValues) {
            query.availableValues = [];
            return;
        }
        query.availableValues = Object.keys(pmc.specificValues).sort((a, b) => {
            return pmc.translationValues[a] < pmc.translationValues[b] ? -1 : 1;
        });
        return;
    }

    getText(saveToCache: boolean = true): Promise<void> {
        return new Promise((resolve, reject) => {
            this.currentText = '';
            if (this.helperService.isVocabularyCheck) {
                return resolve();
            } else {
                this.getCTStextPassage(this.currentUrn).then((ar: AnnisResponse) => {
                    this.processAnnisResponse(ar, saveToCache);
                    return resolve();
                }, async (error: HttpErrorResponse) => {
                    return reject(error);
                });
            }
        });
    }

    getTranslations(): void {
        this.translate.get('DATA_ALREADY_SENT').subscribe(value => this.dataAlreadySentMessage = value);
        this.translate.get('DATA_SENT').subscribe(value => this.dataSentSuccessMessage = value);
        this.translate.get('DOWNLOAD_STARTS_SHORTLY').subscribe(value => this.downloadStartsShortlyString = value);
        this.translate.get('ERROR_CITATIONS_UNAVAILABLE').subscribe(value => this.citationsUnavailableString = value);
        this.translate.get('ERROR_CORPORA_UNAVAILABLE').subscribe(value => this.corporaUnavailableString = value);
        this.translate.get('EXERCISE_FEEDBACK_CORRECT_DEFAULT').subscribe(value => this.exercise.feedback.correct = value);
        this.translate.get('EXERCISE_FEEDBACK_GENERAL_DEFAULT').subscribe(value => this.exercise.feedback.general = value);
        this.translate.get('EXERCISE_FEEDBACK_INCORRECT_DEFAULT').subscribe(value => this.exercise.feedback.incorrect = value);
        this.translate.get('EXERCISE_FEEDBACK_PARTIALLY_CORRECT_DEFAULT').subscribe((value) => {
            this.exercise.feedback.partiallyCorrect = value;
        });
        this.translate.get(ExerciseTypeTranslation[this.exercise.type]).subscribe(value => this.exercise.typeTranslation = value);
        this.translate.get(InstructionsTranslation[this.exercise.type]).subscribe((value) => {
            this.exercise.instructionsTranslation = value;
        });
        this.translate.get('INVALID_QUERY_CORPUS').subscribe(value => this.invalidQueryCorpusString = value);
        this.translate.get('INVALID_SENTENCE_COUNT').subscribe(value => this.invalidSentenceCountString = value);
        this.translate.get('INVALID_TEXT_RANGE').subscribe(value => this.invalidTextRangeString = value);
        this.translate.get('LINK_COPIED').subscribe(value => this.shareLinkCopiedString = value);
        this.translate.get('MY_TEXT').subscribe(value => this.myTextString = value);
        this.translate.get('QUERY_VALUE_EMPTY').subscribe(value => this.emptyQueryValueString = value);
        this.translate.get('SEARCH_REGEX_MISSING').subscribe(value => this.searchRegexMissingString = value);
        this.translate.get('TEXT_TOO_LONG').subscribe(value => this.textTooLongString = value + configMC.maxTextLength);
        this.translate.get('TOO_MANY_SEARCH_RESULTS').subscribe(value => this.tooManyHitsString = value);
    }

    initCorpusService(): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            this.isInitialized = new ReplaySubject<boolean>(1);
            this.initCurrentCorpus().then();
            this.initCurrentTextRange();
            this.helperService.initUpdateInfo().then(() => {
                this.checkForUpdates().finally(() => {
                    this.checkAnnisResponse().then(() => {
                        this.restoreLastCorpus().then(() => {
                            return resolve();
                        });
                    }, () => {
                        return resolve();
                    });
                });
            }, () => {
                return reject();
            });
            this.initPhenomenonMap();
            this.translate.onLangChange.subscribe(() => {
                this.getTranslations();
            });
        });
    }

    initCurrentCorpus(): Promise<void> {
        return new Promise((resolve) => {
            this.currentCorpus = new ReplaySubject<CorpusMC>(1);
            if (!this.currentCorpusCache) {
                this.helperService.applicationState.pipe(take(1)).subscribe((state: ApplicationState) => {
                    this.currentCorpusCache = state.mostRecentSetup.currentCorpus ?
                        state.mostRecentSetup.currentCorpus : null;
                    if (this.currentCorpusCache) {
                        this.currentCorpus.next(this.currentCorpusCache);
                    }
                    return resolve();
                });
            } else {
                this.currentCorpus.next(this.currentCorpusCache);
                return resolve();
            }
        });
    }

    initCurrentTextRange(): void {
        this.currentTextRange = new ReplaySubject<TextRange>(1);
        this.helperService.applicationState.pipe(take(1)).subscribe((state: ApplicationState) => {
            this.currentTextRangeCache = state.mostRecentSetup.currentTextRange;
            this.currentTextRange.next(this.currentTextRangeCache);
        });
    }

    initPhenomenonMap(): void {
        // map the different phenomena to their respective Enum for processing and display/translation
        Object.values(Phenomenon).forEach((value: string) => {
            if (value !== Phenomenon.Lemma) {
                const pmc: PhenomenonMapContent = this.phenomenonMap[value];
                pmc.translationValues = {};
                const translationObject: object = pmc.translationObject;
                Object.keys(translationObject).forEach((k: string) => {
                    if (k !== k.toUpperCase()) {
                        this.translate.get(translationObject[k]).subscribe(v => pmc.translationValues[k] = v);
                    }
                });
            }
        });
    }

    isTreebank(corpus: CorpusMC): boolean {
        return corpus.source_urn.includes('proiel');
    }

    public loadCorporaFromLocalStorage(): Promise<void> {
        return new Promise<void>(resolve => {
            this.storage.get(configMC.localStorageKeyCorpora).then((jsonString: string) => {
                if (jsonString) {
                    this.processCorpora(this.getCorpusListFromJSONstring(jsonString));
                }
                return resolve();
            });
        });
    }

    parseUrnWithTextRange(urn: string): [string, TextRange] {
        const urnParts: string[] = urn.split('@');
        const baseUrnParts: string[] = urnParts[0].split(':');
        const textRangeParts: string[] = baseUrnParts.slice(-1)[0].split('-');
        const startParts: string[] = textRangeParts[0].split('.');
        const endParts: string[] = textRangeParts[1].split('.');
        return [baseUrnParts.slice(0, -1).join(':'), { start: startParts, end: endParts }];
    }

    processAnnisResponse(ar: AnnisResponse, saveToCache: boolean = true): void {
        Object.keys(this.phenomenonMap).forEach((key: string) => {
            const pmc: PhenomenonMapContent = this.phenomenonMap[key];
            pmc.specificValues = {};
        });
        this.phenomenonMap.lemma.translationValues = {};
        this.processNodes(ar);
        const pointingLinks: Link[] = ar.graph_data.links.filter(x => x.annis_component_type === 'Pointing');
        pointingLinks.forEach((link: Link) => {
            const dep: DependencyValue = this.helperService.dependencyMap[link.udep_deprel];
            if (dep) {
                const existingValue = this.phenomenonMap.dependency.specificValues[dep];
                this.phenomenonMap.dependency.specificValues[dep] = (existingValue ? existingValue : 0) + 1;
            }
        });
        // need to add root dependencies manually because they are tricky to handle
        const nodeIds: string[] = ar.graph_data.nodes.map(x => x.id);
        const nodesWithDependencySet: Set<string> = new Set<string>(pointingLinks.map(x => x.target));
        const rootNodeIds: string[] = nodeIds.filter(x => !nodesWithDependencySet.has(x));
        this.phenomenonMap.dependency.specificValues[DependencyValue.root] = rootNodeIds.length;
        this.adjustQueryValue(this.exercise.queryItems[0], 0);
        // remove whitespace before punctuation
        this.currentText = ar.graph_data.nodes.map(x => x.annis_tok).join(' ').replace(/[ ]([.,\/#!$%\^&\*;:{}=\-_`~()])/g, (x: string) => {
            return x.trim();
        });
        this.annisResponse = ar;
        if (saveToCache) {
            this.helperService.applicationState.pipe(take(1)).subscribe((as: ApplicationState) => {
                as.mostRecentSetup = {
                    annisResponse: ar,
                    currentUrn: this.currentUrn,
                    currentAuthor: this.currentAuthor,
                    currentCorpus: this.currentCorpusCache,
                    currentTextRange: this.currentTextRangeCache
                };
                this.helperService.saveApplicationState(as).then();
            });
        }
    }

    processCorpora(corpusList: CorpusMC[]): void {
        this.availableCorpora = [];
        this.availableAuthors = [];
        corpusList.forEach((corpus: CorpusMC) => {
            corpus.citations = {};
            this.availableCorpora.push(corpus);
            if (this.isTreebank(corpus)) {
                corpus.author += ' (PROIEL)';
            }
            const existingAuthor: Author = this.availableAuthors.find(author => author.name === corpus.author);
            if (existingAuthor) {
                existingAuthor.corpora.push(corpus);
            } else {
                this.availableAuthors.push(new Author({
                    name: corpus.author,
                    corpora: [corpus]
                }));
            }
        });
        this.availableAuthors.sort((author1, author2) => {
            if (author1.name < author2.name) {
                return -1;
            } else if (author1.name > author2.name) {
                return 1;
            }
        });
        this.availableAuthors.forEach((author) => {
            author.corpora.sort((corpus1, corpus2) => {
                if (corpus1.title < corpus2.title) {
                    return -1;
                } else if (corpus1.title > corpus2.title) {
                    return 1;
                }
                return 0;
            });
        });
    }

    processNodes(ar: AnnisResponse): void {
        ar.graph_data.nodes.forEach((node: NodeMC) => {
            let existingValue = this.phenomenonMap.lemma.specificValues[node.udep_lemma];
            this.phenomenonMap.lemma.specificValues[node.udep_lemma] = (existingValue ? existingValue : 0) + 1;
            this.phenomenonMap.lemma.translationValues[node.udep_lemma] = node.udep_lemma;
            if (node.udep_feats) {
                const featsParts: string[] = node.udep_feats.split('|');
                const casePart: string = featsParts.find(x => x.toLowerCase().includes(FeatsKey.case.toString()));
                if (casePart) {
                    const caseAbbreviation: string = casePart.split('=')[1];
                    const caseValue: CaseValue = this.helperService.caseMap[caseAbbreviation];
                    existingValue = this.phenomenonMap.feats.specificValues[caseValue];
                    this.phenomenonMap.feats.specificValues[caseValue] = (existingValue ? existingValue : 0) + 1;
                }
            }
            const pos: PartOfSpeechValue = this.helperService.partOfSpeechMap[node.udep_upos];
            existingValue = this.phenomenonMap.upos.specificValues[pos];
            this.phenomenonMap.upos.specificValues[pos] = (existingValue ? existingValue : 0) + 1;
        });
    }

    restoreLastCorpus(): Promise<void> {
        return new Promise((resolve, reject) => {
            this.helperService.applicationState.pipe(take(1)).subscribe((state: ApplicationState) => {
                this.annisResponse = state.mostRecentSetup.annisResponse;
                this.currentUrn = state.mostRecentSetup.currentUrn;
                this.currentCorpusCache = state.mostRecentSetup.currentCorpus;
                this.currentTextRangeCache = state.mostRecentSetup.currentTextRange;
                this.previewAnnisResponse = state.previewAnnisResponse;
                this.isTextRangeCorrect = true;
                if (this.annisResponse && this.annisResponse.graph_data && this.annisResponse.graph_data.nodes.length) {
                    this.processAnnisResponse(this.annisResponse, false);
                    return resolve();
                } else if (this.currentText) {
                    // check if the data is already present
                    return resolve();
                } else {
                    const saveToCache: boolean = !state.mostRecentSetup.annisResponse ||
                        !state.mostRecentSetup.annisResponse.graph_data ||
                        !state.mostRecentSetup.annisResponse.graph_data.nodes.length;
                    this.getText(saveToCache).then(() => {
                        return resolve();
                    }, () => {
                        return reject();
                    });
                }
            });
        });
    }

    setCurrentCorpus(corpus: CorpusMC): void {
        this.currentCorpusCache = corpus;
        this.currentCorpus.next(this.currentCorpusCache);
        this.setCurrentTextRange(-1, null, new TextRange({ start: ['', '', ''], end: ['', '', ''] }));
        this.helperService.applicationState.pipe(take(1)).subscribe((state: ApplicationState) => {
            state.mostRecentSetup.currentCorpus = corpus;
            state.mostRecentSetup.currentTextRange = this.currentTextRangeCache;
            this.helperService.saveApplicationState(state).then();
        });
    }

    setCurrentTextRange(inputId: number = -1, newValue: string = null, tr: TextRange = null): void {
        if (tr) {
            this.currentTextRangeCache = tr;
        } else if (inputId >= 0) {
            const isStart: boolean = inputId < 4;
            const targetInputId = `input${inputId}`;
            if (newValue === null) {
                newValue = document.querySelector<HTMLInputElement>(`#${targetInputId}`).value;
            }
            const trIdx: number = inputId - (isStart ? 1 : 4);
            // set the new value for the current text range indirectly, depending on the relevant index
            const relevantTextRangePart: string[] = isStart ? this.currentTextRangeCache.start : this.currentTextRangeCache.end;
            relevantTextRangePart[trIdx] = newValue;
        }
        this.currentTextRange.next(this.currentTextRangeCache);
    }

    /**
     * Updates the base word for the current exercise phenomenon.
     * @param query The query item to update.
     * @param queryIndex The index of the query to update.
     */
    updateBaseWord(query: QueryMC, queryIndex: number): void {
        if (!this.annisResponse || !this.annisResponse.frequency_analysis || !this.annisResponse.frequency_analysis.length) {
            return;
        }
        if (!queryIndex && this.exercise.type === ExerciseType.matching) {
            this.getSortedQueryValues(this.exercise.queryItems[1], 1);
            if (!query.selectedValues.length) {
                const fi: FrequencyItem = this.annisResponse.frequency_analysis.find(
                    x => x.values[0] === this.exercise.queryItems[0].selectedValues[0]);
                this.exercise.queryItems[1].phenomenon = fi.phenomena[1];
            }
            this.adjustQueryValue(this.exercise.queryItems[1], 1);
        }
    }
}
